import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircleIcon,
  ArrowPathIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/24/outline";
import { CheckIcon } from "@heroicons/react/20/solid";
import { PENDING_PLATFORM_UPGRADE_KEY } from "../../hooks/useBillingSetupRedirect";

type StepStatus = {
  complete: boolean;
  total?: number;
  pending?: number;
  eligible?: number;
  subscribed?: number;
  needingPaymentMethod?: number;
  onManualBilling?: number;
};

type BillingSetupStatus = {
  showWizard: boolean;
  isComplete: boolean;
  dismissed: boolean;
  upgradedFromFree: boolean;
  steps: {
    platformUpgrade: StepStatus;
    stripeConnect: StepStatus;
    syncMemberships: StepStatus;
    exportStudents: StepStatus;
    autoSubscribe: StepStatus;
  };
  lastRun: {
    at: string;
    results: {
      membershipsSynced: number;
      studentsExported: number;
      studentsAutoSubscribed: number;
      studentsSkippedManual: number;
      studentsNeedingPaymentMethod: number;
      errors: string[];
    };
  } | null;
};

type BillingSetupWizardProps = {
  getAuthToken: () => string | null;
  csrfToken: string;
  forceShow?: boolean;
  onDismiss?: () => void;
};

function StepRow({
  title,
  description,
  complete,
  action,
  detail,
}: {
  title: string;
  description: string;
  complete: boolean;
  action?: React.ReactNode;
  detail?: string;
}) {
  return (
    <li className="relative flex gap-4 pb-8 last:pb-0">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 border-indigo-600 bg-white dark:bg-gray-800">
        {complete ? (
          <CheckIcon className="h-5 w-5 text-indigo-600" aria-hidden="true" />
        ) : (
          <span className="h-2.5 w-2.5 rounded-full bg-indigo-600" />
        )}
      </div>
      <div className="min-w-0 flex-1 pt-0.5">
        <p className="text-sm font-semibold text-gray-900 dark:text-white">
          {title}
        </p>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          {description}
        </p>
        {detail && (
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
            {detail}
          </p>
        )}
        {action && <div className="mt-3">{action}</div>}
      </div>
    </li>
  );
}

export default function BillingSetupWizard({
  getAuthToken,
  csrfToken,
  forceShow = false,
  onDismiss,
}: BillingSetupWizardProps) {
  const [status, setStatus] = useState<BillingSetupStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    const authToken = getAuthToken();
    if (!authToken) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/stripe/billing-setup/status`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            "X-CSRF-Token": csrfToken,
          },
        }
      );

      if (!response.ok) {
        if (response.status === 403) {
          setStatus(null);
          return;
        }
        throw new Error("Failed to load billing setup status");
      }

      const data = (await response.json()) as BillingSetupStatus;
      setStatus(data);
      setError(null);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load setup status"
      );
    } finally {
      setLoading(false);
    }
  }, [csrfToken, getAuthToken]);

  useEffect(() => {
    if (csrfToken) {
      fetchStatus();
    }
  }, [csrfToken, fetchStatus]);

  useEffect(() => {
    const onFocus = () => fetchStatus();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [fetchStatus]);

  const handleRunSetup = async () => {
    const authToken = getAuthToken();
    if (!authToken) return;

    setRunning(true);
    setError(null);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/stripe/billing-setup/run`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${authToken}`,
            "X-CSRF-Token": csrfToken,
            "Content-Type": "application/json",
          },
        }
      );

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Billing setup failed");
      }

      if (data.status) {
        setStatus(data.status);
      } else {
        await fetchStatus();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Billing setup failed");
    } finally {
      setRunning(false);
    }
  };

  const handleDismiss = async () => {
    const authToken = getAuthToken();
    if (!authToken) return;

    setDismissing(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/stripe/billing-setup/dismiss`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${authToken}`,
            "X-CSRF-Token": csrfToken,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to dismiss wizard");
      }

      onDismiss?.();
      sessionStorage.removeItem(PENDING_PLATFORM_UPGRADE_KEY);
      setStatus((prev) =>
        prev ? { ...prev, showWizard: false, dismissed: true } : prev
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to dismiss");
    } finally {
      setDismissing(false);
    }
  };

  if (loading || (forceShow && !status)) {
    return (
      <div className="mb-6 overflow-hidden rounded-xl border border-indigo-200 dark:border-indigo-800 bg-gradient-to-br from-indigo-50 to-white dark:from-indigo-950/40 dark:to-gray-800 shadow-sm animate-pulse">
        <div className="border-b border-indigo-100 dark:border-indigo-900/60 px-5 py-4">
          <div className="h-5 w-64 rounded bg-indigo-100 dark:bg-indigo-900/40" />
          <div className="mt-2 h-4 w-full max-w-xl rounded bg-indigo-50 dark:bg-indigo-900/20" />
        </div>
        <div className="space-y-6 px-5 py-5">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex gap-4">
              <div className="h-8 w-8 shrink-0 rounded-full bg-indigo-100 dark:bg-indigo-900/40" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-40 rounded bg-gray-200 dark:bg-gray-700" />
                <div className="h-3 w-full max-w-md rounded bg-gray-100 dark:bg-gray-800" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (!status) return null;
  if (!forceShow && !status.showWizard) return null;

  const { steps, lastRun } = status;
  const canRunAutomatedSteps =
    steps.stripeConnect.complete &&
    ((steps.syncMemberships.pending ?? 0) > 0 ||
      (steps.exportStudents.pending ?? 0) > 0 ||
      (steps.autoSubscribe.eligible ?? 0) > 0);

  const automatedComplete =
    steps.syncMemberships.complete &&
    steps.exportStudents.complete &&
    steps.autoSubscribe.complete;

  const runLabel = running
    ? "Running setup..."
    : lastRun
      ? "Run again"
      : "Sync memberships & students";

  return (
    <div className="mb-6 overflow-hidden rounded-xl border border-indigo-200 dark:border-indigo-800 bg-gradient-to-br from-indigo-50 to-white dark:from-indigo-950/40 dark:to-gray-800 shadow-sm">
      <div className="border-b border-indigo-100 dark:border-indigo-900/60 px-5 py-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
              Finish setting up student billing
            </h3>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              Your DojoTrack plan is active. Connect Stripe and migrate your
              free-tier memberships and students so automated billing can take
              over.
            </p>
          </div>
          {automatedComplete && (
            <CheckCircleIcon className="h-7 w-7 shrink-0 text-emerald-500" />
          )}
        </div>
      </div>

      <div className="px-5 py-5">
        {error && (
          <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
            <ExclamationCircleIcon className="mt-0.5 h-5 w-5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <ol className="space-y-0">
          <StepRow
            title="Platform plan upgraded"
            description="Your studio is on a paid DojoTrack plan with Stripe billing enabled."
            complete={steps.platformUpgrade.complete}
          />

          <StepRow
            title="Connect Stripe"
            description="Link your Stripe account so DojoTrack can bill students on your behalf."
            complete={steps.stripeConnect.complete}
            detail={
              steps.stripeConnect.complete
                ? "Stripe Connect is linked."
                : "Required before syncing memberships or students."
            }
            action={
              !steps.stripeConnect.complete ? (
                <Link
                  to="/app/settings/integrations#stripe"
                  className="inline-flex items-center rounded-lg bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-500"
                >
                  Connect in Integrations
                </Link>
              ) : undefined
            }
          />

          <StepRow
            title="Sync memberships to Stripe"
            description="Creates Stripe products and prices for membership types you defined on the free plan."
            complete={steps.syncMemberships.complete}
            detail={
              steps.syncMemberships.total === 0
                ? "No membership types to sync."
                : steps.syncMemberships.complete
                  ? `All ${steps.syncMemberships.total} membership type(s) linked to Stripe.`
                  : `${steps.syncMemberships.pending} of ${steps.syncMemberships.total} membership type(s) still need Stripe prices.`
            }
          />

          <StepRow
            title="Export students to Stripe"
            description="Creates a Stripe customer record for each active student in your roster."
            complete={steps.exportStudents.complete}
            detail={
              steps.exportStudents.total === 0
                ? "No active students to export."
                : steps.exportStudents.complete
                  ? `All ${steps.exportStudents.total} active student(s) have Stripe customer records.`
                  : `${steps.exportStudents.pending} student(s) still need Stripe customer records.`
            }
          />

          <StepRow
            title="Start automated billing"
            description="Students with a card on file get subscribed automatically. Cash/manual students stay on manual billing."
            complete={steps.autoSubscribe.complete}
            detail={
              [
                steps.autoSubscribe.subscribed
                  ? `${steps.autoSubscribe.subscribed} already on Stripe subscriptions`
                  : null,
                (steps.autoSubscribe.eligible ?? 0) > 0
                  ? `${steps.autoSubscribe.eligible} ready to auto-subscribe`
                  : null,
                (steps.autoSubscribe.needingPaymentMethod ?? 0) > 0
                  ? `${steps.autoSubscribe.needingPaymentMethod} need a payment method (use {{payment_link}} in SMS/email)`
                  : null,
                (steps.autoSubscribe.onManualBilling ?? 0) > 0
                  ? `${steps.autoSubscribe.onManualBilling} on cash/manual billing (unchanged)`
                  : null,
              ]
                .filter(Boolean)
                .join(" · ") || undefined
            }
            action={
              steps.stripeConnect.complete &&
              !automatedComplete &&
              canRunAutomatedSteps ? (
                <button
                  type="button"
                  onClick={handleRunSetup}
                  disabled={running}
                  className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500 disabled:opacity-50"
                >
                  {running && (
                    <ArrowPathIcon className="h-4 w-4 animate-spin" />
                  )}
                  {runLabel}
                </button>
              ) : steps.stripeConnect.complete &&
                !automatedComplete &&
                !canRunAutomatedSteps ? (
                <button
                  type="button"
                  onClick={handleRunSetup}
                  disabled={running}
                  className="inline-flex items-center gap-2 rounded-lg border border-indigo-300 dark:border-indigo-600 px-4 py-2 text-sm font-semibold text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 disabled:opacity-50"
                >
                  {running && (
                    <ArrowPathIcon className="h-4 w-4 animate-spin" />
                  )}
                  Mark migration complete
                </button>
              ) : undefined
            }
          />
        </ol>

        {lastRun && (
          <div className="mt-5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-900/40 p-4 text-sm">
            <p className="font-medium text-gray-900 dark:text-white">
              Last run results
            </p>
            <ul className="mt-2 space-y-1 text-gray-600 dark:text-gray-400">
              <li>
                {lastRun.results.membershipsSynced} membership(s) synced to
                Stripe
              </li>
              <li>
                {lastRun.results.studentsExported} student(s) exported to
                Stripe
              </li>
              <li>
                {lastRun.results.studentsAutoSubscribed} student(s)
                auto-subscribed
              </li>
              {(lastRun.results.studentsNeedingPaymentMethod ?? 0) > 0 && (
                <li>
                  {lastRun.results.studentsNeedingPaymentMethod} still need a
                  payment method
                </li>
              )}
              {(lastRun.results.studentsSkippedManual ?? 0) > 0 && (
                <li>
                  {lastRun.results.studentsSkippedManual} left on cash/manual
                  billing
                </li>
              )}
            </ul>
            {lastRun.results.errors?.length > 0 && (
              <div className="mt-3 text-xs text-amber-700 dark:text-amber-300">
                {lastRun.results.errors.slice(0, 3).map((msg) => (
                  <p key={msg}>{msg}</p>
                ))}
                {lastRun.results.errors.length > 3 && (
                  <p>…and {lastRun.results.errors.length - 3} more</p>
                )}
              </div>
            )}
          </div>
        )}

        {(automatedComplete ||
          (steps.stripeConnect.complete && !canRunAutomatedSteps)) && (
          <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-indigo-100 dark:border-indigo-900/60 pt-4">
            {(steps.autoSubscribe.needingPaymentMethod ?? 0) > 0 && (
              <p className="flex-1 text-sm text-gray-600 dark:text-gray-400">
                Send families a message with the{" "}
                <code className="rounded bg-gray-100 px-1 dark:bg-gray-700">
                  {"{{payment_link}}"}
                </code>{" "}
                tag from Messaging or SMS to collect cards.
              </p>
            )}
            <button
              type="button"
              onClick={handleDismiss}
              disabled={dismissing}
              className="rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
            >
              {dismissing ? "Saving..." : "Done for now"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
